/**
 * Drive-mode resources (FE-01 FR-FE01-09, today `pararManejo`): everything the drive screen starts
 * (GPS watch, speech recognition, speech synthesis, screen wake lock) is registered here, and
 * `stopDriveMode()` releases all of it when the route is left (`onLeave`) or unmounted. FE-10 builds
 * the screen on these helpers; while drive mode runs, app updates wait (FE-04 FR-FE04-12).
 */
import { blockUpdates } from "../../../shared/offline/updateGate";

type Stop = () => void;

const stops = new Set<Stop>();
let releaseUpdateBlock: (() => void) | null = null;

/** Register a resource's stop function; returns an unregister function. */
export function trackDriveResource(stop: Stop): () => void {
  stops.add(stop);
  return () => stops.delete(stop);
}

/** Drive mode is on: block app updates until it stops. */
export function startDriveMode(): void {
  if (!releaseUpdateBlock) releaseUpdateBlock = blockUpdates("drive");
}

export function isDriveModeActive(): boolean {
  return releaseUpdateBlock !== null;
}

/** `navigator.geolocation.watchPosition`, cleared by `stopDriveMode`. */
export function watchDrivePosition(
  onPosition: PositionCallback,
  onError?: PositionErrorCallback,
  options?: PositionOptions,
): (() => void) | null {
  const geo = typeof navigator !== "undefined" ? navigator.geolocation : undefined;
  if (!geo?.watchPosition) return null;
  const id = geo.watchPosition(onPosition, onError, options);
  const stop = () => geo.clearWatch(id);
  trackDriveResource(stop);
  return stop;
}

/** A speech recognition instance (Web Speech API), aborted by `stopDriveMode`. */
export function trackRecognition(recognition: { abort: () => void }): () => void {
  return trackDriveResource(() => recognition.abort());
}

/** A screen wake lock, released by `stopDriveMode`. */
export function trackWakeLock(lock: { release: () => Promise<void> }): () => void {
  return trackDriveResource(() => void lock.release().catch(() => undefined));
}

/** Stop GPS, recognition, speech and the wake lock; let app updates through again. */
export function stopDriveMode(): void {
  for (const stop of [...stops]) {
    try {
      stop();
    } catch {
      // A resource that already ended must not keep the others running.
    }
  }
  stops.clear();
  try {
    if (typeof window !== "undefined" && "speechSynthesis" in window)
      window.speechSynthesis.cancel();
  } catch {
    // ignore
  }
  releaseUpdateBlock?.();
  releaseUpdateBlock = null;
}
